
import { useState, useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';

import {
  generalInfoQuery,
  consolidatedScoresInsightsQuery,
  consolidatedInsightsQuery,
} from '../api';

import './styles.css';

import { navigation } from '../data';
import SubPagesLayout from '../../../UI/layout/subPages';
import PopupMessages from '../../../UI/introGameLayout/popupMessages';
import Scores from '../../../UI/game/scores';
import InstructorTransactionsModal from '../../../UI/instructor/decisionInsights/transactionsModal';

import {
  popupMessagesDecisionInsightsDoubleEntry,
  popupMessagesDecisionInsightsNoGamesData,
  summaryDecisionBreakdownElementsToDiplay,
  decisionBreakdownElementsHeadersDoubleEntry
} from './data';

export default function InstructorInsightsDoubleEntry() {
  const navigate = useNavigate();

  const [isModalShown, setIsModalShown] = useState(false);
  const [isDetailsButtonLoading, setIsDetailsButtonLoading] = useState(false);
  const [shouldFetchDetails, setShouldFetchDetails] = useState(false);
  const [filterBy, setFilterBy] = useState('Accuracy');
  const [data, setData] = useState([]);
  const [transactionsData, setTransactionsData] = useState([]);

  const [popupMessages, setPopupMessages] = useState([]);
  const [detailsButtonText, setDetailsButtonText] = useState(null);

  const filterByOptions = ['Accuracy', 'Points'];

  const { data: generalInfo } = useQuery(generalInfoQuery()) || {};
  const { data: consolidatedScoresInsights } = useQuery(consolidatedScoresInsightsQuery({ completed: true }));
  const { data: consolidatedInsights } = useQuery({
    ...consolidatedInsightsQuery(),
    enabled: shouldFetchDetails,
  });

  const games = generalInfo?.games;

  const equationScoresInsights = useMemo(() => consolidatedScoresInsights?.balanced_equation || [], [consolidatedScoresInsights]);

  useEffect(() => {
    if(consolidatedScoresInsights) {
      formatLeaderboardData();
    }
  }, [consolidatedScoresInsights, filterBy]);

  const calculateAverage = (data, key, prop) => {
    if (!data || data.length === 0) return 0;

    let sum = 0;
    let count = 0;

    data.forEach(record => {
      const value = record[key]?.[prop];
      if (value !== null && value !== undefined) {
        sum += value;
        count++;
      }
    });

    return count > 0 ? sum / count : 0;
  };

  const formatLeaderboardData = () => {
    const increaseDecreaseElementsScore = filterBy === 'Accuracy'
      ? calculateAverage(equationScoresInsights, 'accuracy', 'increase_decrease_elements')
      : calculateAverage(equationScoresInsights, 'points', 'increase_decrease_elements');
    const increaseDecreaseAccountsScore = filterBy === 'Accuracy'
      ? calculateAverage(equationScoresInsights, 'accuracy', 'increase_decrease_accounts')
      : calculateAverage(equationScoresInsights, 'points', 'increase_decrease_accounts');
    const debitCreditsScore = filterBy === 'Accuracy'
      ? calculateAverage(equationScoresInsights, 'accuracy', 'debits_credits')
      : calculateAverage(equationScoresInsights, 'points', 'debits_credits');

    const sectionData = [
      ...(increaseDecreaseElementsScore ? [{ title: "Increase or Decrease (Elements)", score: increaseDecreaseElementsScore * 100 || 0, color: '#EAE4F9' }] : []),
      ...(debitCreditsScore ? [{ title: "Debits & Credits", score: debitCreditsScore * 100 || 0, color: '#EAE4F9' }] : []),
      ...(increaseDecreaseAccountsScore ? [{ title: "Increase or Decrease (Accounts)", score: increaseDecreaseAccountsScore * 100 || 0, color: '#EAE4F9' }] : []),
    ];

    const data = [
      ...(sectionData.length > 0 ? [{
        title: 'Accuracy By Decision Type',
        bars: sectionData,
        barsLabel: '%'
      }] : []),
    ];

    setData(data);
  }

  // Gate the popup off the same scores-insights data that paints the
  // chart. generalInfo.sessions['completed'] uses a stricter "whole
  // game finished" rule than the scores endpoint, so the two sources
  // can disagree (chart shows numbers while popup says "Hang tight").
  // Reading both from the same array eliminates that drift.
  useEffect(() => {
    if (equationScoresInsights.length > 0) {
      setPopupMessages(popupMessagesDecisionInsightsDoubleEntry);
      setDetailsButtonText('View Details');
    } else if (consolidatedScoresInsights) {
      setPopupMessages(popupMessagesDecisionInsightsNoGamesData);
    }
  }, [consolidatedScoresInsights, equationScoresInsights]);

  const addCellToRow = (row, optionItemName, value, isCorrect, backgroundColor) => {
    if (summaryDecisionBreakdownElementsToDiplay.includes(optionItemName)) return;
    const cell = { value, isCorrect, background: backgroundColor };
    if (row[optionItemName]) {
      row[optionItemName].push(cell);
    } else {
      row[optionItemName] = [cell];
    }
  };

  useEffect(() => {
    if (consolidatedInsights && shouldFetchDetails) {
      const equationInsights = consolidatedInsights.balanced_equation || [];
      const transactionsData = [];

      equationInsights.forEach(insight => {
        const row = {
          question: insight.question,
        };

        (insight.item_options || []).forEach(itemOption => {
          const headerName = itemOption.item_option;

          (itemOption.selected_change_options || []).forEach(sco => {
            const isCorrect = sco.is_correct;

            // Type 1: Increase/Decrease (selected_change is null, selected_option is Increase or Decrease)
            if (sco.selected_change === null && (sco.selected_option === 'Increase' || sco.selected_option === 'Decrease')) {
              let value;
              let backgroundColor = isCorrect ? '#D1E9D6' : '#FAEDED';
              if (!isCorrect && sco.count_users >= 5) {
                value = `<span class="white">${sco.selected_option}<span class="decision-insights-count">${sco.count_users}</span></span>`;
                backgroundColor = '#FD9595';
              } else {
                value = sco.selected_option;
              }
              addCellToRow(row, headerName, value, isCorrect, backgroundColor);
            }

            // Type 2: Debit/Credit (selected_change is null, selected_option is Debit or Credit)
            if (sco.selected_change === null && (sco.selected_option === 'Debit' || sco.selected_option === 'Credit')) {
              let value;
              let backgroundColor = isCorrect ? '#D1E9D6' : '#FAEDED';
              if (!isCorrect && sco.count_users >= 5) {
                value = `<span class="white">${sco.selected_option}<span class="decision-insights-count">${sco.count_users}</span></span>`;
                backgroundColor = '#FD9595';
              } else {
                value = sco.selected_option;
              }
              addCellToRow(row, headerName, value, isCorrect, backgroundColor);
            }

            // Type 3: Account-level (selected_change is not null, e.g. selected_option="Cash", selected_change="Increase")
            if (sco.selected_change !== null) {
              const displayValue = `${sco.selected_change} ${sco.selected_option}`;
              let value;
              let backgroundColor = isCorrect ? '#D1E9D6' : '#FAEDED';
              if (!isCorrect && sco.count_users >= 5) {
                value = `<span class="white">${displayValue}<span class="decision-insights-count">${sco.count_users}</span></span>`;
                backgroundColor = '#FD9595';
              } else {
                value = displayValue;
              }
              addCellToRow(row, headerName, value, isCorrect, backgroundColor);
            }
          });
        });

        transactionsData.push(row);
      });

      setTransactionsData(transactionsData);
      setIsModalShown(true);
      setIsDetailsButtonLoading(false);
    }
  }, [consolidatedInsights, shouldFetchDetails]);

  const handlePrevButtonClick = () => {
    let redirectUrl = '/instructor/insights/decision-insights/overall';
    let navigateState = undefined;
    if(games && games.length > 0) {
      // classified-multistep sits between classifying-normal and
      // double-entry now, so Back lands there whenever the course has
      // any Account Classification game. Pass direction so multistep
      // can keep travelling back to classifying-normal if it has
      // nothing to show.
      if(games.some(game => game.game_type === 'Account Classification')) {
        redirectUrl = '/instructor/insights/decision-insights/classified-multistep';
        navigateState = { direction: 'back' };
      } else if(games.some(game => game.game_type === 'Normal Balance')) {
        redirectUrl = '/instructor/insights/decision-insights/classifying-normal';
      }
    }
    navigate(redirectUrl, navigateState ? { state: navigateState } : undefined);
  }

  const handleNextButtonClick = () => {
    let redirectUrl = '/instructor/insights/progress-scores/progress';
    if(games && games.length > 0) {
      if(games.some(game => game.game_type === 'The Principles')) {
        redirectUrl = '/instructor/insights/decision-insights/principles';
      }
    }
    navigate(redirectUrl);
  }

  const handleFilterClick = (selectedFilterBy) => {
    setFilterBy(selectedFilterBy);
  }

  const maxScore = Math.max(...data.flatMap(section =>
    section.bars?.map(bar => bar.score) || []
  ));

  const handleDetailsButtonClick = () => {
    setIsDetailsButtonLoading(true);
    setShouldFetchDetails(true);
  }

  const handleModalCloseButtonClick = () => {
    setIsModalShown(false);
  }

  return (
    <SubPagesLayout
      navigation={navigation}
      onFirstButtonClick={handlePrevButtonClick}
      onSecondButtonClick={handleNextButtonClick}
      currentPage={4}
      pagesQuantity={5}
      contentClassName="decision-insights-content-container instructor-decision-insights-content-container"
    >
      <h1>INSIGHTS | OVERALL <b>| DOUBLE ENTRY</b></h1>
      <div className="decision-insights-content">
        <PopupMessages
          messages={popupMessages}
          messageSize="xx-small"
          buttonText="Next"
          onButtonClick={handleNextButtonClick}
          additionalButtonText="Back"
          onAdditionalButtonClick={handlePrevButtonClick}
          isAvatarOnTop={true}
        />
        <Scores
          data={data}
          size="small"
          maxScore={maxScore}
          isBigScores={true}
          alignBarTitle="center"
          detailsButtonText={detailsButtonText}
          onDetailsButtonClick={handleDetailsButtonClick}
          isDetailsButtonLoading={isDetailsButtonLoading}
        />
        {isModalShown && (
          <InstructorTransactionsModal
            onCloseButtonClick={handleModalCloseButtonClick}
            tableHeaders={decisionBreakdownElementsHeadersDoubleEntry}
            transactionsData={transactionsData}
          />
        )}
      </div>
    </SubPagesLayout>
  );
}
